import * as Sentry from "@sentry/react";
import type { ErrorInfo } from "react";
import i18next from "i18next";
import type { DomTranslationGuardOp } from "@/lib/domTranslationGuard";
import { redactSecretPathsDeep } from "@/lib/secretPaths";

let initialized = false;

// Chrome marks <html> while it translates the page, and both Chrome's and
// Edge's translators wrap their text in <font>, which Jynio never renders.
function isPageTranslated(): boolean {
  const { classList } = document.documentElement;
  return (
    classList.contains("translated-ltr") ||
    classList.contains("translated-rtl") ||
    document.querySelector("font") !== null
  );
}

/**
 * Initialize Sentry on the frontend. No-ops if VITE_SENTRY_DSN is unset
 * (dev / preview) or if already initialized (so it's safe to call from both the
 * consent-boot path and on a later consent grant — #171). Gated behind the
 * analytics consent by applyConsentToSdks; never call it before consent.
 */
export function initSentry() {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  // Never report from the dev server even if VITE_SENTRY_DSN is set locally —
  // HMR/dev-server fetch aborts ("Load failed" on lvh.me) otherwise flood the
  // issue stream. Set VITE_SENTRY_FORCE_DEV=1 to test Sentry from a dev box.
  if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_FORCE_DEV) return;
  initialized = true;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // The deploy that served this page (layouts render it from FLY_IMAGE_REF,
    // the same release Rails reports). A tab left open across deploys keeps
    // its old release, which is how a stale-build error announces itself.
    release: document.querySelector<HTMLMetaElement>('meta[name="jynio-release"]')?.content || undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "0.05"),
    // Benign browser/ActionCable churn: the WebSocket is torn down before its
    // open handshake completes when a user navigates away or the network blips.
    // Not actionable — was the bulk of the weekly error volume.
    ignoreErrors: ["WebSocket closed without opened"],
    // A crash on a page the browser was translating is usually the
    // translator's doing (JYNIO-WEB-E, -WEB-M); say so on every event, with
    // the language Jynio itself was showing.
    beforeSend(event) {
      event.tags = {
        ...event.tags,
        page_translated: String(isPageTranslated()),
        ...(i18next.language ? { ui_locale: i18next.language } : {}),
      };
      return redactSecretPathsDeep(event);
    },
    // Reset and confirmation links carry a secret in their address (#1079), and
    // it sits in many places of an event: request.url, the Referer, trace
    // context (url.full, url.path), navigation breadcrumbs, `extra`. One pass
    // over every string, rather than a list of fields the next SDK would outgrow.
    beforeSendTransaction: (event) => redactSecretPathsDeep(event),
    beforeBreadcrumb: (breadcrumb) => redactSecretPathsDeep(breadcrumb),
  });
}

let domTranslationGuardReported = false;

// The DOM guard (lib/domTranslationGuard.ts) stops a translated page from
// crashing, so those errors never reach Sentry again. A breadcrumb per hit,
// plus one message per page load, shows how often it saves a page. Before
// consent Sentry is off: nothing is recorded, and the one message stays
// unspent for a hit after consent.
export function reportDomTranslationGuardHit(op: DomTranslationGuardOp) {
  if (!initialized) return;
  Sentry.addBreadcrumb({ category: "dom.translation-guard", message: op, level: "info" });
  if (domTranslationGuardReported) return;
  domTranslationGuardReported = true;
  Sentry.captureMessage("DOM translation guard kept a translated page from crashing", {
    level: "info",
    fingerprint: ["dom-translation-guard"],
    tags: { op },
  });
}

export interface NoticeOptions {
  // Never "error": nothing failed, and error level is how Sentry tells us
  // something broke. Use captureError for real exceptions.
  level: "info" | "warning";
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  fingerprint?: string[];
}

// Report something worth knowing that isn't an exception (a 404, a missing CMS
// page) as a plain message: no synthetic stack, and a level that says how much
// it matters. No-op when Sentry isn't configured, like captureError.
export function captureNotice(message: string, options: NoticeOptions) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureMessage(message, options);
}

// Report a caught exception. No-op when Sentry isn't configured, so callers
// don't need to guard. A `fingerprint` replaces Sentry's own grouping, for an
// error whose message varies while its cause does not.
export function captureError(error: unknown, context?: Record<string, unknown>, fingerprint?: string[]) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureException(error, context || fingerprint ? {extra: context, fingerprint} : undefined);
}

// Report an error an ErrorBoundary caught. captureReactException chains the
// component stack onto the error as its cause, which Sentry source-maps; sent
// as an extra it arrived minified, and the scrubber replaced it with
// "[Filtered]".
export function captureBoundaryError(error: unknown, info: ErrorInfo) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.captureReactException(error, info);
}

// Tag later events with the signed-in account, so an issue says who was hit.
// The id only: it finds the account in admin, and Sentry needs no email.
export function setSentryUser(user: { id: string } | null) {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  Sentry.setUser(user ? { id: user.id } : null);
}
